import { useState } from "react";
import { MoveHorizontal, ArrowBigUp, Zap, Beer, PartyPopper, Dog, Flag, Trophy, Play } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";

const T = {
  es: {
    title: "CÓMO JUGAR",
    move: "Moverse", moveK: "◄ ► o A / D (o botones en el cel)",
    jump: "Saltar", jumpK: "ESPACIO o ▲ — ¡algunos hacen doble/triple salto!",
    power: "Poder", powerK: "K o X — ataque especial de tu personaje",
    beer: "Medidor de FIESTA", beerD: "Recogé cervezas 🍺 para llenar el medidor de FIESTA.",
    dance: "¡Bailá para el especial!", danceD: "Con el medidor lleno, bailá en la PISTA DE BAILE para activar el MODO FIESTA: ¡invencible y a todo poder!",
    avoid: "Cuidado", avoidD: "Esquivá chuchos y chancletas. Saltales encima para derrotarlos.",
    goal: "Meta", goalD: "Llegá a la piñata al final del nivel para ganar.",
    ctaTitle: "¡Creá tu usuario!",
    ctaBody: "Guardá tus MEJORES PUNTAJES y ganá PREMIOS GRATIS.",
    cta: "CREAR USUARIO",
    play: "JUGAR",
    dontShow: "No mostrar de nuevo",
  },
  en: {
    title: "HOW TO PLAY",
    move: "Move", moveK: "◄ ► or A / D (or on-screen buttons)",
    jump: "Jump", jumpK: "SPACE or ▲ — some do double/triple jumps!",
    power: "Power", powerK: "K or X — your character's special attack",
    beer: "FIESTA meter", beerD: "Collect beers 🍺 to fill the FIESTA meter.",
    dance: "Dance for the special!", danceD: "When the meter is full, DANCE on the DANCE FLOOR to trigger FIESTA MODE: invincible and powered up!",
    avoid: "Watch out", avoidD: "Dodge street dogs and flying sandals. Stomp them from above to beat them.",
    goal: "Goal", goalD: "Reach the piñata at the end of the level to win.",
    ctaTitle: "Create a username!",
    ctaBody: "Save your TOP SCORES and win FREE PRIZES.",
    cta: "CREATE USERNAME",
    play: "PLAY",
    dontShow: "Don't show this again",
  },
};

const Row = ({ icon, title, desc, accent }) => (
  <div className="flex items-start gap-3">
    <div className="shrink-0 mt-0.5 rounded-lg p-2" style={{ backgroundColor: accent }}>{icon}</div>
    <div className="min-w-0">
      <div className="font-pixel text-[11px] text-white">{title}</div>
      <div className="font-vt text-base text-white/70 leading-tight">{desc}</div>
    </div>
  </div>
);

export default function HowToPlay({ character, onPlay, onAuth }) {
  const { lang } = useLang();
  const { user } = useAuth();
  const t = T[lang] || T.es;
  const [dontShow, setDontShow] = useState(false);

  return (
    <div className="absolute inset-0 z-[85] overflow-y-auto overscroll-contain flex items-start justify-center bg-black/85 p-4 scanlines" data-testid="howto-modal">
      <div className="pixel-card bg-[#141414] w-full max-w-lg p-5 md:p-6 max-h-full overflow-y-auto pop-in my-auto">
        <h2 className="font-pixel text-lg text-[#FFEB3B] pixel-stroke text-center mb-4">{t.title}</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
          <Row accent="#0067C6" icon={<MoveHorizontal size={18} className="text-white" />} title={t.move} desc={t.moveK} />
          <Row accent="#4CAF50" icon={<ArrowBigUp size={18} className="text-white" />} title={t.jump} desc={t.jumpK} />
          <Row accent="#9c27b0" icon={<Zap size={18} className="text-white" />} title={t.power} desc={t.powerK} />
          <Row accent="#FFB300" icon={<Beer size={18} className="text-black" />} title={t.beer} desc={t.beerD} />
          <div className="sm:col-span-2 rounded-xl border-2 border-[#FF5722] bg-[#FF5722]/10 p-2">
            <Row accent="#FF5722" icon={<PartyPopper size={18} className="text-white" />} title={t.dance} desc={t.danceD} />
          </div>
          <Row accent="#777" icon={<Dog size={18} className="text-white" />} title={t.avoid} desc={t.avoidD} />
          <Row accent="#E91E63" icon={<Flag size={18} className="text-white" />} title={t.goal} desc={t.goalD} />
        </div>

        {!user?.username && (
          <div className="mt-4 rounded-xl border-2 border-[#FFEB3B]/40 bg-[#FFEB3B]/5 p-3 flex items-center gap-3" data-testid="howto-cta">
            <Trophy size={26} className="text-[#FFEB3B] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-pixel text-[10px] text-[#FFEB3B]">{t.ctaTitle}</div>
              <div className="font-vt text-base text-white/80 leading-tight">{t.ctaBody}</div>
            </div>
            <button data-testid="howto-signup-btn" onClick={onAuth} className="pixel-btn bg-[#0067C6] text-white px-3 py-2 text-[10px] shrink-0">{t.cta}</button>
          </div>
        )}

        <button data-testid="howto-play-btn" onClick={() => onPlay(dontShow)} className="pixel-btn bg-[#4CAF50] text-white w-full px-6 py-3.5 text-sm mt-5 flex items-center justify-center gap-2">
          <Play size={18} strokeWidth={3} /> {t.play}
        </button>

        <label className="flex items-center justify-center gap-2 mt-3 cursor-pointer" data-testid="howto-dontshow">
          <input type="checkbox" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)} className="w-4 h-4 accent-[#FF5722]" data-testid="howto-dontshow-checkbox" />
          <span className="font-vt text-base text-white/55">{t.dontShow}</span>
        </label>
      </div>
    </div>
  );
}
